import React, {cloneElement, Component} from 'react'
import {connect} from 'react-redux'
import propTypes from 'prop-types'
import {get, has, isEqual, isNil, omit, set, isEmpty} from 'lodash'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Divider from '@material-ui/core/Divider'
import iconNames from '../../theme/icons'
import _Date from '../../containers/date'
import NumberString from '../../containers/numberString'
import PivotTable, {extractState} from '../../containers/pivotTable'
import {pivotTableColumnPropType, pivotTablePropTypes} from 'containers/pivotTable'
import Button from '../../components/button'
import Loading from 'components/loading'
import NewItem from 'components/newItem'
import BulkEditor from './components/bulkEditor'
import CellEditor from './components/cellEditor'
import Hintable from '../../components/hintable'
import Icon from '../../components/icon'
import NumberField from '../../components/numberField'
import {addEntity, editData, requestData, saveData} from "actions/pivotTable"
import {getRegionalLocale} from 'selectors/settings'
import {getAllSelectedRows} from 'selectors/pivotTable'
import {prefix} from 'helpers/component'
import {t} from "helpers/language"
import StringHelper from '../../helpers/string'
import {setScrollTop} from 'helpers/ui'
import {getColumnOptions, getVisibleColumns, setEntityEdited} from 'helpers/pivotTable'
import {parseCssSelector} from 'helpers/css'
import {getIds} from 'helpers/general/lists'

const classPrefix = 'editablePivotTable'
require('./editablePivotTable.scss')

export const formatValue = (entity, column, value, locale, forExport = false) => {
  let dataType = column.dataType || 'String'
  if (column.dataTypeForEntity) dataType = column.dataTypeForEntity(entity)

  switch (dataType) {
    default:
    case 'String':
    case 'Memo':
    case 'Phone':
    case 'Mobile':
    case 'Email':
      return isNil(value) ? '' : value

    case 'Number': {
      if (isNil(value) || value === '')
        return ''

      const columnOptions = getColumnOptions(entity, column)
      const accuracy = get(columnOptions, 'accuracy', 0)
      const prepend = get(columnOptions, 'prepend', '')
      const append = get(columnOptions, 'append', '')

      if (forExport)
        return `${prepend}${StringHelper.formatNumber(value, locale, accuracy)}${append}`

      return <div>{prepend}<NumberString precision={accuracy}>{value}</NumberString>{append}</div>
    }
    case 'List': {
      if (column.values) {
        const option = column.values.find(v => v.value === value)
        if (option) return option.label
      } else {
        console.error(`Column '${column.key}' does not provide any values!`)
      }
      return value
    }
    case 'Multi List': {
      if (column.values) {
        if (value && value.map) {
          const options = value.map(val => (column.values.find(v => v.value === val) || {}).label)
          return options.join(', ')
        }
      } else {
        console.error(`Column '${column.key}' does not provide any values!`)
      }
      return value
    }
    case 'Lookup': {
      return value ? value.label : ''
    }
    case 'Company List': {
      return value ? value.label : ''
    }
    case 'Boolean': {
      if (!isNil(value)) {
        if (value === true) return 'Yes'
        if (value === false) return 'No'
      }
      return value
    }
    case 'Date': {
      if (!isNil(value)) return <_Date>{value}</_Date>
      return value
    }
  }
}

class EditablePivotTable extends Component {
  constructor(props) {
    super(props)
    this.state = {
      currentCell: null,
      selectedCells: [],
      selectedRowIds: props.selectedRowIds || [],  // now an array of entity IDs
      selectingRange: false,
      selectingRangeStart: null,
      actionsOpen: false,
      actionsAnchor: null,
      bulkEditing: false,
      clipboard: null,
      addData: props.addObject,
      activeDataset: null,  // if different to props.data
      activeLevel: 0,       // current level of expansion when using sub-data
      isAdding: false,
    }
  }

  componentDidMount = () => {
    addEventListener('mouseup', this.cancelDrag)
    addEventListener('keydown', this.keyDown)
  }

  componentWillUnmount = () => {
    removeEventListener('mouseup', this.cancelDrag)
    removeEventListener('keydown', this.keyDown)
  }

  componentDidUpdate = (prevProps) => {
    const {data, selectedRowIds, addObject, onChangeSelectedRows} = this.props
    if (selectedRowIds) {
      if (!isEqual(selectedRowIds, prevProps.selectedRowIds)) {
        this.setState({selectedRowIds, selectedCells: []})
      }

    } else {

      // Reset selected rows/cells if data is changed
      if (this.hasChanged(prevProps.data, data)) {
        this.setState({selectedRowIds: [], selectedCells: []})
        if (onChangeSelectedRows) onChangeSelectedRows([], false)
      }
    }

    if (!isEqual(prevProps.addObject, addObject)) {
      this.setState({addData: addObject})
    }
  }

  getClass = prefix(classPrefix + '-')

  hasChanged = (oldData, newData) => {
    const {showSubData, subDataProp} = this.props

    const isOldNil = isNil(oldData)
    const isNewNil = isNil(newData)
    if (isOldNil !== isNewNil) return true
    if (!isOldNil && !isNewNil) {
      if (oldData.length !== newData.length) return true

      // Check for missing data or changed order
      return oldData.some(od => {
        const nd = newData.find(d => d.id === od.id)
        if (nd && showSubData && subDataProp) {
          // recursive check for sub-data
          return this.hasChanged(od[subDataProp], nd[subDataProp])
        }
        return !nd
      })
    }
    return false
  }

  keyDown = (e) => {
    switch (e.keyCode) {
      default:
        break
    }
  }

  setCell = (e, x, y, level) => {
    if (level === this.state.activeLevel) {
      if (!this.cells) this.cells = []
      if (!this.cells[x]) this.cells[x] = []
      this.cells[x][y] = e
    }
  }

  showBulkEditor = () => {
    this.setState({
      actionsOpen: false,
      bulkEditing: true,
    })
  }

  hideBulkEditor = () => {
    this.setState({bulkEditing: false})
  }

  getData = () => {
    const {data} = this.props
    const {activeDataset} = this.state
    return activeDataset || data
  }

  // The rows as they are laid out, which is what a row index refers to
  getRows = () => {
    const {onData} = this.props

    return onData ? onData(this.getData()) : this.getData()
  }

  getDataLength = () => this.getRows().length

  getSelectedData = () => {
    const {selectedRows} = this.props
    const {selectedRowIds} = this.state
    const entityIds = isEmpty(selectedRowIds)
      ? getIds(selectedRows)
      : selectedRowIds

    return this.getData().filter(entity => entityIds.includes(entity.id) && entity.deleted !== true)
  }

  bulkEdit = (editedData) => {
    const {autoSave, columns, onCanEdit, onSave} = this.props
    let selectedData = this.getSelectedData()

    Object.keys(editedData).forEach(key => {
      const column = columns[key]
      const value = editedData[key]
      if (column) {
        selectedData = selectedData.map(e => {
          if (onCanEdit && !onCanEdit(e, column)) return
          this.change(column, e.id, e, value, null, false)
          return {...e, [key]: value}
        })
      }
    })

    this.setState({bulkEditing: false})
    if (autoSave) onSave()
  }

  showActions = (e) => {
    this.setState({
      actionsOpen: true,
      actionsAnchor: e.currentTarget,
    })
  }

  hideActions = () => {
    this.setState({actionsOpen: false})
  }

  bulkAction = (e, action) => {
    if (action.onClick(e, this.getSelectedData()) !== false) this.hideActions()
  }

  selectAllRows = () => {
    const {onChangeSelectedRows, onCanEdit, onSelectAllRows} = this.props
    const selectedRowIds = this.getData()
      .filter(entity => {
        return !(onCanEdit && !onCanEdit(entity))
      })
      .map(entity => entity.id)

    onSelectAllRows()
    this.setState({
      selectedRowIds,
    }, () => {
      if (onChangeSelectedRows) onChangeSelectedRows(selectedRowIds, true)
    })
  }

  deSelectAllRows = () => {
    const {onChangeSelectedRows, onDeSelectAllRows} = this.props
    if (onDeSelectAllRows) onDeSelectAllRows()
    this.setState({
      selectedRowIds: [],
    }, () => {
      if (onChangeSelectedRows) onChangeSelectedRows([], false)
    })
  }

  toggleSelectedRow = (entity, startIndex, endIndex, toggled) => {
    let {selectedRowIds} = this.state
    const {data, onChangeSelectedRows, onSelectAllRows, onDeSelectAllRows, areAllRowsSelected} = this.props

    const exists = selectedRowIds.includes(entity.id)
    if (toggled) {
      if (!exists) selectedRowIds.push(entity.id)
      if (data.length === selectedRowIds.length) onSelectAllRows()
    } else if (exists) {
      selectedRowIds = selectedRowIds.filter(id => id !== entity.id)
      onDeSelectAllRows()
    }

    this.setState({selectedRowIds})
    if (onChangeSelectedRows) onChangeSelectedRows(selectedRowIds, areAllRowsSelected)
  }

  moveCell = (curX, curY, moveX, moveY, wrap = false, edit = false) => {
    let newX = curX + moveX
    let newY = curY + moveY
    if (wrap) {
      if (newX < 0 && newY > 0) {
        newX = this.cells.length - 1
        newY--
      } else if (newX >= this.cells.length) {
        newX = 0
        newY++

        const {allowAdd} = this.props
        const dataLength = this.getData().length
        if (allowAdd && newY >= dataLength) {
          this.add(true, (newY > dataLength))
          return
        }
      }
    }
    this.focusCell(newX, newY, edit)
  }

  focusCell = (x, y, edit = false) => {
    try {
      this.clearRange()
      const nextCell = this.cells[x][y]
      nextCell.focus()
      if (edit) nextCell.edit()
    } catch (e) {
      console.error(e)
    }
  }

  selectRange = (x1, x2, y1, y2, selected = true, append = false) => {
    const {visibleColumns} = this.props
    const dataLength = this.getData().length

    if (x2 < x1) {
      const lowX = x2
      x2 = x1
      x1 = lowX
    }

    if (y2 < y1) {
      const lowY = y2
      y2 = y1
      y1 = lowY
    }

    if (x1 < 0) x1 = 0
    if (x2 >= visibleColumns.length) x2 = visibleColumns.length - 1
    if (y1 < 0) y1 = 0
    if (y2 >= dataLength) y2 = dataLength - 1

    const selectedCells = append ? [...this.state.selectedCells] : []
    for (let x = x1; x <= x2; x++) {
      for (let y = y1; y <= y2; y++) {
        set(selectedCells, [x, y], selected)
      }
    }
    this.setState({selectedCells})
  }

  clearRange = () => {
    this.setState({selectedCells: []})
  }

  getCurrentRange = () => {
    const {currentCell, selectedCells} = this.state
    let minX = null, maxX = null, minY = null, maxY = null

    for (let x = 0; x < selectedCells.length; x++) {
      if (selectedCells[x]) {
        for (let y = 0; y < selectedCells[x].length; y++) {
          if (selectedCells[x][y]) {
            if (minX === null) minX = x
            if (minY === null) minY = y
            if (maxX === null || x > maxX) maxX = x
            if (maxY === null || y > maxY) maxY = y
          }
        }
      }
    }

    if (currentCell) {
      if (minX === null) minX = currentCell.x
      if (maxX === null) maxX = currentCell.x
      if (minY === null) minY = currentCell.y
      if (maxY === null) maxY = currentCell.y
    }

    return {minX, maxX, minY, maxY}
  }

  getRangeData = () => {
    const {columns, visibleColumns} = this.props
    const data = this.getData()
    let {selectedCells} = this.state
    const {currentCell} = this.state
    const ret = []

    if (selectedCells.length === 0 && currentCell) {
      selectedCells = []
      selectedCells[currentCell.x] = []
      selectedCells[currentCell.x][currentCell.y] = true
    }

    for (let x = 0; x < selectedCells.length; x++) {
      if (selectedCells[x]) {
        for (let y = 0; y < selectedCells[x].length; y++) {
          if (selectedCells[x][y]) {
            const entity = data[y]
            const column = columns[visibleColumns[x]]
            ret.push({entity, column})
          }
        }
      }
    }

    return ret
  }

  selectRelativeRange = (moveX, moveY) => {
    // Find relative cell based on current selected range and root
    const {currentCell} = this.state
    const range = this.getCurrentRange()

    if (currentCell && range.minX < currentCell.x) {
      range.minX += moveX
    } else {
      range.maxX += moveX
    }

    if (currentCell && range.minY < currentCell.y) {
      range.minY += moveY
    } else {
      range.maxY += moveY
    }

    this.selectRange(range.minX, range.maxX, range.minY, range.maxY)
  }

  selectCell = (x, y, rangeSelect, appendSelect, clearRange) => {
    const {currentCell, selectedCells} = this.state

    if (currentCell && rangeSelect) {
      // Select a range
      this.selectRange(currentCell.x, x, currentCell.y, y)

    } else if (currentCell && appendSelect) {
      // Append to range
      this.selectRange(x, x, y, y, !get(selectedCells, [x, y], false), true)

    } else {
      if (clearRange) this.clearRange()
      this.setState({currentCell: {x, y}})
    }
  }

  focusFirstRow = (rangeSelect) => {
    const {currentCell} = this.state
    if (currentCell) {
      if (rangeSelect) {
        this.selectCell(currentCell.x, 0, true, false, true)
      } else {
        this.focusCell(currentCell.x, 0)
      }
    }
  }

  focusLastRow = (rangeSelect) => {
    const {currentCell} = this.state
    const data = this.getData()

    if (currentCell && data) {
      if (rangeSelect) {
        this.selectCell(currentCell.x, data.length - 1, true, false, true)
      } else {
        this.focusCell(currentCell.x, data.length - 1)
      }
    }
  }

  focusFirstCell = (rangeSelect) => {
    const {currentCell} = this.state
    if (currentCell) {
      if (rangeSelect) {
        this.selectCell(0, currentCell.y, true, false, true)
      } else {
        this.focusCell(0, currentCell.y)
      }
    }
  }

  focusLastCell = (rangeSelect) => {
    const {currentCell} = this.state
    if (currentCell && this.cells) {
      if (rangeSelect) {
        this.selectCell(this.cells.length - 1, currentCell.y, true, false, true)
      } else {
        this.focusCell(this.cells.length - 1, currentCell.y)
      }
    }
  }

  cellKeyDown = (e) => {
    switch (e.keyCode) {
      case 8: // BACKSPACE
      case 127: // DEL
        this.deleteRange()
        return false

      // CTRL/CMD+C = Copy
      case 67:
        if (e.ctrlKey || e.metaKey) {
          this.copy()
          return false
        }
        break

      // CTRL/CMD+D = Fill Down
      case 68:
        if (e.ctrlKey || e.metaKey) {
          this.fillDown()
          return false
        }
        break

      // CTRL/CMD+V = Paste
      case 86:
        if (e.ctrlKey || e.metaKey) {
          this.paste()
          return false
        }
        break

      // CTRL/CMD+X = Cut
      case 88:
        if (e.ctrlKey || e.metaKey) {
          this.cut()
          return false
        }
        break

      // CTRL/CMD+S = Save
      case 83:
        if (e.ctrlKey || e.metaKey) {
          this.props.onSave()
          return false
        }
        break

      // CTRL/CMD+Z = Revert All Changes
      case 90:
        if (e.ctrlKey || e.metaKey) {
          this.props.onRevert()
          return false
        }
        break

      // END / CTRL+END
      case 35:
        if (e.ctrlKey) {
          this.focusLastRow(e.shiftKey)
        } else {
          this.focusLastCell(e.shiftKey)
        }
        return false

      // HOME / CTRL+HOME
      case 36:
        if (e.ctrlKey) {
          this.focusFirstRow(e.shiftKey)
        } else {
          this.focusFirstCell(e.shiftKey)
        }
        return false

      // CMD+Left
      case 37:
        if (e.metaKey) {
          this.focusFirstCell(e.shiftKey)
          return false
        }
        break

      // CMD+Right
      case 39:
        if (e.metaKey) {
          this.focusLastCell(e.shiftKey)
          return false
        }
        break

      // CMD+Up
      case 38:
        if (e.metaKey) {
          this.focusFirstRow(e.shiftKey)
          return false
        }
        break

      // CMD+Down
      case 40:
        if (e.metaKey) {
          this.focusLastRow(e.shiftKey)
          return false
        }
        break
    }
  }

  cellMouseDown = (e, colIndex, rowIndex) => {
    this.selectingRange = true
    this.selectingRangeStart = {x: colIndex, y: rowIndex}
    this.selectingRangeCurrent = {x: colIndex, y: rowIndex}
  }

  cellMouseMove = (e, colIndex, rowIndex) => {
    if (this.selectingRange && this.selectingRangeStart) {
      if (colIndex !== this.selectingRangeCurrent.x || rowIndex !== this.selectingRangeCurrent.y) {
        const {x, y} = this.selectingRangeStart
        const append = (e.ctrlKey || e.metaKey)
        this.selectRange(x, colIndex, y, rowIndex, true, append)
        this.selectingRangeCurrent = {x: colIndex, y: rowIndex}
      }
    }
  }

  cancelDrag = () => {
    this.selectingRange = false
  }

  fillDown = () => {
    const rangeData = this.getRangeData()
    const copiedData = {}

    rangeData.forEach(({column, entity}) => {
      if (!has(copiedData, column.key)) {
        copiedData[column.key] = column.data(entity) // use the value of the first entity
      } else {
        const value = copiedData[column.key]
        this.change(column, entity.id, entity, value, null, false)
      }
    })

    const {autoSave, onSave} = this.props
    if (autoSave) onSave()
  }

  deleteRange = () => {
    const rangeData = this.getRangeData()
    rangeData.forEach(({column, entity}) => {
      this.change(column, entity.id, entity, null, null, false)
    })

    const {autoSave, onSave} = this.props
    if (autoSave) onSave()
  }

  cut = () => {
    const rangeData = this.getRangeData()
    const clipboard = {}

    rangeData.forEach(({column, entity}) => {
      if (!has(clipboard, column.key)) {
        clipboard[column.key] = column.data(entity) // use the value of the first entity
      }
      this.change(column, entity.id, entity, null, null, false)
    })
    this.setState({clipboard})

    const {autoSave, onSave} = this.props
    if (autoSave) onSave()
  }

  copy = () => {
    const rangeData = this.getRangeData()
    const clipboard = {}

    rangeData.forEach(({column, entity}) => {
      if (!has(clipboard, column.key)) {
        clipboard[column.key] = column.data(entity) // use the value of the first entity
      }
    })
    this.setState({clipboard})
  }

  paste = () => {
    const {clipboard} = this.state
    const rangeData = this.getRangeData()

    if (clipboard && rangeData) {
      rangeData.forEach(({column, entity}) => {
        let value = clipboard[column.key]
        if (!has(clipboard, column.key)) {
          // allows for copy/paste across different columns
          value = clipboard[Object.keys(clipboard)[0]]
        }
        this.change(column, entity.id, entity, value, null, false)
      })
    }

    const {autoSave, onSave} = this.props
    if (autoSave) onSave()
  }

  add = (focus = true, commit = true) => {
    const addData = {
      ...this.props.addFallbacks,
      ...omit(this.state.addData, ['isNewRow']),
    }
    // since we are changing the data length, getting the length now
    // prevents race conditions affecting where we focus
    const preAddDataLength = this.getDataLength()
    const preAddIds = this.props.focusAddedRow ? this.getRows().map((entity) => entity.id) : []

    if (commit && addData) {
      this.props.onAdd(addData)
      this.setState(
        {addData: this.props.addObject, isAdding: false}, // reset to initial state
        () => {
          this.scrollToBottom()
              focus && this.focusOnRowIndex(this.getAddedRowIndex(preAddIds, preAddDataLength))
        })
    } else {
      this.setState(
        {isAdding: true},
        () => {
          this.scrollToBottom()
          this.focusOnRowIndex(preAddDataLength)
        })
    }
  }

  scrollToBottom = () => setScrollTop(10000000, 0)

  // A new row is appended, so the appended index is where it lands. An owner that reorders the data
  // as it arrives - resource modes sort their dormant rows to the bottom - can set focusAddedRow to
  // follow the row that is new to the data instead of whichever row ended up last. Falls back to the
  // appended index when the added row cannot be identified.
  getAddedRowIndex = (preAddIds, appendedIndex) => {
    if (!this.props.focusAddedRow) return appendedIndex

    const addedIndex = this.getRows().findIndex((entity) => !preAddIds.includes(entity.id))

    return addedIndex < 0 ? appendedIndex : addedIndex
  }

  focusOnRowIndex = (rowIndex) => {
    const firstEditColumn = this.props.visibleColumns.map(c => this.props.columns[c]).findIndex(c => c.editable)
    if (firstEditColumn >= 0) {
      this.focusCell(firstEditColumn, rowIndex, true)
    }
  }

  change = (column, key, entity, value, meta, allowAutoSave = true, callback = () => {
  }) => {
    if (this.props.clearWithEmptyString && isNil(value)) value = ''
    if (column.setData) {
      if (entity.isNewRow) {
        if (!isNil(value) && value !== '') {
          entity = {...this.props.addObject, ...entity}  // merge in default settings for new entities
          const commitEdit = column.setData(entity, value, meta)
          if (commitEdit !== false) {
            this.setState({addData: entity}, () => callback(true))
          }
        }
      } else {

        const getValueFromColumn = (column, entity) => {
          const columnValue = column.data ? column.data(entity) : ''
          return isNil(columnValue) ? '' : columnValue
        }

        const curValue = getValueFromColumn(column, entity)
        const newEntity = {...entity}
        const commitEdit = column.setData(newEntity, value, meta)

        if (commitEdit !== false && curValue !== getValueFromColumn(column, newEntity)) {
          setEntityEdited(newEntity, column.key)
          this.props.onEdit(key, newEntity, allowAutoSave)
          callback(false)
        }
      }
    } else {
      console.error(
        'WARNING! Column \''
        + column.key
        + '\' does not handle editing, make sure you implement the setData() function'
      )
    }
  }

  translateActions = (actions) => {
    if (actions) {
      const {selectedCells, currentCell} = this.state
      return actions.map(action => ({
        ...action,
        disabled: action.disabled || (action.rangeAction && currentCell === null && selectedCells.length === 0),
        onClick: action.onClick ? (e) => action.onClick(e, this.getSelectedData(), this.getRangeData()) : null,
        rangeAction: null,
        actions: this.translateActions(action.actions),
      }))
    }
  }

  setActiveDataset = (activeDataset, activeLevel) => {
    this.cells = null
    this.setState({
      activeDataset,
      activeLevel,
      selectedRowIds: [],
      selectedCells: [],
    }, () => {
      this.props.onDeSelectAllRows()
    })
  }

  getNewItem = (index, label, action) => {
    return <NewItem
      className={parseCssSelector(label)}
      key={index}
      label={label}
      icon={<Icon name={action.icon} color={action.iconColor}/>}
      onClick={(e) => this.bulkAction(e, action)}/>
  }

  removeCheckBoxSelectFunctions = (props) => {
    const updatedProps = {...props}
    delete updatedProps.onSelectAllRows
    delete updatedProps.onDeSelectAllRows
    return updatedProps
  }

  onIsRowSelected = (entity, level) => {
    const {selectedRowIds, activeLevel} = this.state
    return level === activeLevel && selectedRowIds.includes(entity.id)
  }

  render() {
    const {
      mode,
      areAllRowsSelected,
      allowDelete,
      columns,
      visibleColumns,
      actions,
      autoSave,
      data,
      onSave,
      onRevert,
      onCanEdit,
      onCanSelect,
      edited,
      locale,
      canSelectRows,
      allowAdd,
      allowSave,
      allowRevert,
      allowBulkEdit,
      allowCut,
      allowCopy,
      allowPaste,
      allowFillDown,
      fixedRowAddButtonLabel,
      hideFixedRowAddButtonWhilstAdding,
      bulkActions,
      controlColumns = [],
      onIsRowSelected,
      onSelectEntity,
      onDeleteEntity,
      columnSelectAllowed,
      onIsLoading,
      ...otherProps
    } = this.props
    const {
      activeLevel,
      actionsOpen,
      actionsAnchor,
      bulkEditing,
      selectedCells,
      currentCell,
      clipboard,
      addData,
      isAdding,
    } = this.state

    const ptColumns = {}
    Object.keys(columns).forEach(k => {
      const column = columns[k]
      ptColumns[k] = {
        ...column,
        export: column.export || (e => formatValue(e, column, column.data ? column.data(e) : '', locale, true)),
        cellClass: CellEditor,
        cellRef: this.setCell,
        cellProps: {
          onChange: this.change,
          onSelect: this.selectCell,
          getSelected: (x, y, level) => {
            const selected = (level === activeLevel) && get(selectedCells, [x, y], false)
            return {
              selected,
              rangeRight: selected && get(selectedCells, [x - 1, y], false),
              rangeLeft: selected && get(selectedCells, [x + 1, y], false),
              rangeDown: selected && get(selectedCells, [x, y - 1], false),
              rangeUp: selected && get(selectedCells, [x, y + 1], false),
            }
          },
          locale,
          adding: isAdding,
          fixedRowAddButtonLabel,
          hideFixedRowAddButtonWhilstAdding,
          onAdd: this.add,
          onCanEdit,
          onMoveCell: this.moveCell,
          onSelectRange: this.selectRange,
          onSelectRelativeRange: this.selectRelativeRange,
          onClearRange: this.clearRange,
          onMouseDown: this.cellMouseDown,
          onMouseMove: this.cellMouseMove,
          onKeyDown: this.cellKeyDown,
          editBeforePersist: this.props.editBeforePersist,
        },
      }
    })

    const anyEditable = (isNil(onCanEdit) || this.getData().some(entity => onCanEdit(entity)))
    const selectedItems = this.getSelectedData().length
    const itemsText = (selectedItems === 1) ? '1 item' : `${selectedItems} items`

    let selectActions = []
    if (anyEditable && bulkActions && bulkActions.length > 0) {
      if (allowBulkEdit) {
        selectActions.push(
          <NewItem
            className={'edit'}
            key="edit"
            label={selectedItems === 1 ? 'Edit 1 item' : `Bulk Edit ${itemsText}`}
            icon={<Icon name={iconNames.edit}/>}
            onClick={this.showBulkEditor}
          />
        )
        selectActions.push(
          <Divider key="_" className={this.getClass('divider')}/>
        )
      }
      selectActions = [
        ...selectActions,
        ...bulkActions.map((action, index) => {
          if (action.divider) {
            return <Divider key={index} className={this.getClass('divider')}/>
          }
          const label = action.label.replace(/{items}/g, itemsText)
          const showHint = (hint, child, index) => (hint)
            ? (
              <Hintable label={hint} key={index}>
                {child}
              </Hintable>
            )
            : cloneElement(child, {index})

          if (action.label === 'Dormant contacts') {
            return showHint(action.hint, this.getNewItem(index, label, action))
          }
          if (action.label === 'Live contacts') {
            return showHint(action.hint, this.getNewItem(index, label, action))
          }
          return showHint(
            action.hint,
            <MenuItem
              key={index}
              disabled={action.disabled}
              onClick={(e) => this.bulkAction(e, action)}
            >
              <ListItemIcon><Icon name={action.icon} color={action.iconColor}/></ListItemIcon>
              {label}
            </MenuItem>,
            index
          )
        }),
      ]
    }

    const saveDisabled = isNil(edited) || (Object.keys(edited).length === 0)
    const actionsDisabled = selectedItems === 0

    let ptActions = []
    if (autoSave !== true) {
      if (allowSave) {
        ptActions.push({
          tooltip: t('Save'),
          label: saveDisabled ? null : 'Save Changes',
          icon: iconNames.save,
          onClick: onSave,
          primary: true,
          className: this.getClass('save-button'),
          disabled: saveDisabled,
        })
      }
      if (allowRevert) {
        ptActions.push({
          tooltip: t('Revert Changes'),
          icon: iconNames.revert,
          onClick: onRevert,
          primary: true,
          className: this.getClass('revert-button'),
          disabled: saveDisabled,
        })
      }
    }

    if (actions) {
      ptActions = [
        ...ptActions,
        ...this.translateActions(actions),
      ]
    }

    if (selectActions.length > 0) {
      ptActions.push({
        label: 'Actions',
        icon: iconNames.dropdown,
        size: 12,
        labelPosition: 'before',
        disabled: actionsDisabled,
        className: this.getClass('actions-menu'),
        onClick: this.showActions,
      })
    }

    if (ptActions.length > 0)
      ptActions.push({divider: true})

    const currentCellY = get(currentCell, 'y', 0)
    const isCellActionDisabled = (isNil(currentCell) && isEmpty(selectedCells)) || currentCellY === data.length

    if (allowCut)
      ptActions.push({
        tooltip: t('Cut'),
        icon: iconNames.cut,
        disabled: isCellActionDisabled,
        onClick: this.cut,
        className: this.getClass('cut-button'),
      })

    if (allowCopy)
      ptActions.push({
        tooltip: t('Copy'),
        icon: iconNames.copy,
        disabled: isCellActionDisabled,
        onClick: this.copy,
        className: this.getClass('copy-button'),
      })

    if (allowPaste)
      ptActions.push({
        tooltip: t('Paste'),
        icon: iconNames.paste,
        disabled: isNil(clipboard) || isCellActionDisabled,
        onClick: this.paste,
        className: this.getClass('paste-button'),
      })

    if (allowFillDown)
      ptActions.push({
        tooltip: t('Fill Down'),
        icon: iconNames.fillDown,
        size: 18,
        disabled: (selectedCells.length < 1),
        onClick: this.fillDown,
        className: this.getClass('fill-down-button'),
      })

    let fixedDataBottom = []
    if (allowAdd) {
      fixedDataBottom = [{
        ...addData,
        isNewRow: true,
      }]
    }

    const myControlColumns = []
    const myActionColumns = []
    if (onSelectEntity) {
      myControlColumns.push({
        key: 'select',
        label: '',
        size: 30,
        onGetContent: ({key, entity, rowIndex, style, className, isFixed}) => {
          let content = null
          if (!isFixed) {
            if (onIsLoading && onIsLoading(entity)) {
              content = <Loading transparent={true} inline={true} size={0.3}/>
            } else {
              const canSelect = onCanSelect ? onCanSelect(entity) : true
              if (canSelect) {
                const editable = onCanEdit ? onCanEdit(entity) : true
                content = (
                  <Button
                    flat
                    icon={editable ? iconNames.edit : iconNames.view}
                    onClick={() => onSelectEntity(entity, rowIndex)}
                  />
                )
              }
            }
          }
          return (
            <span
              className={`${className} ${this.getClass('selectColumn')}`}
              key={key}
              style={style}
            >
            {content}
          </span>
          )
        },
      })
    }

    if (allowDelete) {
      myActionColumns.push({
        key: 'delete',
        label: '',
        size: 40,
        onGetContent: ({key, entity, rowIndex, style, className, isFixed}) => {
          let editable = true
          if (onCanEdit) editable = onCanEdit(entity)
          const content = isFixed
            ? null
            : (
              <Button
                flat
                icon={editable ? iconNames.delete : iconNames.view}
                onClick={() => onDeleteEntity(entity, rowIndex)}
              />
            )
          return (
            <span
              key={key}
              style={style}
              className={`${className} ${this.getClass('deleteColumn')}`}
            >
            {content}
          </span>
          )
        },
      })
    }

    const _canSelectRows = (canSelectRows || allowBulkEdit || selectActions.length > 0)
    const updatedProps = this.removeCheckBoxSelectFunctions(otherProps)

    return <>
      <div className={this.getClass('container')}>
        <PivotTable
          mode={mode}
          className={classPrefix}
          columns={ptColumns}
          actions={ptActions}
          canSelectRows={_canSelectRows}
          fixedDataBottom={fixedDataBottom}
          areAllRowsSelected={areAllRowsSelected}
          onSelectAllRows={this.selectAllRows}
          onDeSelectAllRows={this.deSelectAllRows}
          onToggleSelectedRow={this.toggleSelectedRow}
          onIsRowSelected={(entity, index, level) => onIsRowSelected
            ? onIsRowSelected(entity, index, level)
            : this.onIsRowSelected(entity, level)}
          controlColumns={[...controlColumns, ...myControlColumns]}
          actionColumns={[...myActionColumns]}
          onCanSelect={onCanEdit ? (entity) => onCanEdit(entity) : undefined}
          onChangeActiveDataset={this.setActiveDataset}
          columnSelectAllowed={columnSelectAllowed}
          {...updatedProps}
        >
          {this.props.children}
        </PivotTable>
      </div>
      <Menu
        open={actionsOpen}
        getContentAnchorEl={null}
        anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
        transformOrigin={{horizontal: 'left', vertical: 'top'}}
        key={'editable-pivot-table-actions-popover'}
        classes={{paper: 'editable-pivot-table-actions-popover'}}
        anchorEl={actionsAnchor}
        onClose={this.hideActions}
        PaperProps={{style: {width: 320}}}
        onClick={(e) => e.stopPropagation()}
        disableRestoreFocus={true}>
          {selectActions}
        </Menu>
      {bulkEditing
        ? (
          <BulkEditor
            open={bulkEditing}
            data={this.getSelectedData()}
            columns={ptColumns}
            visibleColumns={visibleColumns}
            onSave={this.bulkEdit}
            onClose={this.hideBulkEditor}
          />
        )
        : null
      }
    </>
  }
}

EditablePivotTable.defaultProps = {
  autoSave: false,
  allowBulkEdit: true,
  allowSave: true,
  allowRevert: true,
  allowCut: true,
  allowCopy: true,
  allowPaste: true,
  allowFillDown: true,
  columnSelectAllowed: true,
  hideFixedRowAddButton: false,
  allowDelete: false,
  editBeforePersist: true,
  focusAddedRow: false,
  clearWithEmptyString: false,
}

EditablePivotTable.propTypes = {
  areAllRowsSelected: propTypes.bool,
  selectedRows: propTypes.array,
  selectedRowIds: propTypes.array,  // array of item IDs. If omitted, this component will handle this state.
  data: propTypes.array.isRequired,
  columns: propTypes.object.isRequired, // see note below
  edited: propTypes.object.isRequired,
  visibleColumns: propTypes.array.isRequired,
  autoSave: propTypes.bool,
  locale: propTypes.string,
  onEdit: propTypes.func.isRequired,
  onSave: propTypes.func.isRequired,
  onRevert: propTypes.func.isRequired,
  allowAdd: propTypes.bool,
  onAdd: propTypes.func,
  onGetAddOtherData: propTypes.func,
  editBeforePersist: propTypes.bool,
  focusAddedRow: propTypes.bool,  // focus the row that was added rather than the row that ends up last
  onCanEdit: propTypes.func,      // called once: (entity) for each row, and once: (entity, column) for each cell
  onCanSelect: propTypes.func,    // (entity) — gates the pencil/view icon (drawer access)
  onIsLoading: propTypes.func,
  allowSave: propTypes.bool,
  allowRevert: propTypes.bool,
  allowBulkEdit: propTypes.bool,
  allowDelete: propTypes.bool,
  allowCut: propTypes.bool,
  allowCopy: propTypes.bool,
  allowPaste: propTypes.bool,
  allowFillDown: propTypes.bool,
  bulkActions: propTypes.arrayOf(propTypes.shape({
    label: propTypes.string,
    icon: propTypes.string,
    onClick: propTypes.func, // (items: array(number))
    hint: propTypes.string,
  })),
  onIsRowSelected: propTypes.func,
  onChangeSelectedRows: propTypes.func,
  onSelectAllRows: propTypes.func,
  onDeSelectAllRows: propTypes.func,
  canReorderItems: propTypes.bool,
  hideFixedRowAddButton: propTypes.bool,
  onDeleteEntity: propTypes.func,
  fixedRowAddButtonLabel: propTypes.string,
  hideFixedRowAddButtonWhilstAdding: propTypes.bool,
  addDefaults: propTypes.object,
  addFallbacks: propTypes.object,
  clearWithEmptyString: propTypes.bool, // clear cells to '' instead of null (backend treats null as "not provided")
  ...pivotTablePropTypes,
}

// columns defines each column as an object keyed by its key
// e.g. columns: { myColumn: { ... } }
export const columnPropType = {
  ...pivotTableColumnPropType,
  editable: propTypes.bool, // specify true to make this column editable (defaults to false)
  dataType: propTypes.oneOf([ // specifies how to render the data and what editor to use in the cell
    // (required if editable)
    'String',       // free-entry text
    'Memo',         // free-entry multi-line text
    'Phone',        // as String
    'Mobile',       // as String
    'Email',        // as String
    'Number',       // entry of an integer or decimal number, possibly restricted in range
    'List',         // selection of a single item from a restricted list
    'Multi List',   // selection of one or more items from a restricted list
    'Boolean',      // selection of "Yes" or "No"
    'Lookup',       // selection of a single item from a searchable list, possible dynamically loaded
    'Date',         // select a date
    'Time',         // specify a time
    'Date Time',    // specify a date and time
    'Company List', // selection of a single company by searching for it
  ]),
  required: propTypes.bool, // specifies if the value in this column is mandatory (cannot be cleared)
  dataTypeForEntity: propTypes.func, // (entity) return the data type for a specific entity (defaults to dataType)
  setData: propTypes.func, // (entity, newValue,  meta) called when the user enters a new value in this column
                           // (meta specified for list-type columns).
                           // Return false to suppress the commit of the change.
  values: propTypes.arrayOf(propTypes.shape({ // list of possible values (for dataType "List" or "Multi List")
    value: propTypes.string.isRequired,
    label: propTypes.string.isRequired,
  })),
  options: NumberField.propTypes.options, // options which apply when (for dataType "Number" - see NumberField)
  allowNewItems: propTypes.func, // (entity) return true to allow new items to be created (for dataType "Lookup")
  getItems: propTypes.func, // (search) => [{ value, label }]) called when searching return
                            // either a list of objects or a Promise which resolves to a list of objects
                            // (for dataType "Lookup")
  fixedRowAddButtonLabel: propTypes.string,
  hideFixedRowAddButtonWhilstAdding: propTypes.bool,
}

const mapStateToProps = (state, ownProps) => {
  const {columns, stateKey} = ownProps
  const myState = extractState(state, stateKey)

  return {
    edited: myState.edited,
    data: myState.data.data || [],
    visibleColumns: getVisibleColumns(myState, columns),
    locale: getRegionalLocale(state),
    selectedRows: getAllSelectedRows(state, stateKey),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const {stateKey, autoSave, onAdd, onEdit, onGetAddOtherData, onSave} = ownProps
  return {
    onAdd: (entity) => {
      const otherData = onGetAddOtherData ? onGetAddOtherData(entity) : null
      dispatch(addEntity(stateKey, entity, otherData))
      onAdd && onAdd(entity)
    },
    onEdit: (key, entity, allowAutoSave = true) => {
      dispatch(editData(stateKey, key, entity, allowAutoSave && autoSave))
      onEdit && onEdit(entity, allowAutoSave)
    },
    onSave: () => {
      dispatch(saveData(stateKey))
      onSave && onSave()
    },
    onRevert: () => dispatch(requestData(stateKey, true)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps, null, {withRef: true})(EditablePivotTable)
